import React from 'react';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import format from 'date-fns/format';
import enLocale from 'date-fns/locale/en-US';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { FormHelperText } from '@mui/material';
import { createTheme } from '@mui/material/styles';

import { ThemeProvider } from '@mui/material/styles';
import { getWeekStartEnv } from 'helpers/envGetters';
import { ValidationError } from 'helpers/validationRules';
import appTheme from '../../theme';
import { activeTheme } from 'components/FiltersCalendarModal/MobileDatePicker';

const FORMAT_DATE = 'MMMM dd, yyyy';
const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEK_START = getWeekStartEnv();

const Picker = styled(DatePicker)`
  width: 100%;
  justify-content: center;

  input,
  label {
    font-family: Rubik, sans-serif;
  }

  input {
    font-size: 14px;
    color: #354052;
  }
`;
const MobilePicker = styled(DatePicker)`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: flex-start;

  input,
  label {
    font-family: Rubik, sans-serif;
  }

  input {
    font-size: 14px;
    color: #354052;
  }
`;
const PickerIknConsumtion = styled(DatePicker)`
  width: 100%;
  justify-content: center;

  input,
  label {
    font-family: Rubik, sans-serif;
  }

  input {
    font-size: 14px;
    color: #354052;
  }
`;
const theme = (theme: { breakpoints: { only: (string: string) => string | number | symbol } }) =>
  createTheme({
    ...appTheme,
    components: {
      // Most of the old picker components have been deprecated in v5
      // We'll need to update these based on the new component names
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: '#7f8fa4',
            '&:focus': { color: '#7f8fa4' },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            position: 'absolute',
            bottom: '-8px',
            fontSize: '10px',
            whiteSpace: 'nowrap',
            opacity: 0.5,
          },
        },
      },
    },
  });

interface FilterDatePickerProps
  extends Pick<DatePickerProps, Exclude<keyof DatePickerProps, 'error'>> {
  error?: ValidationError | false;
  noHelper?: boolean;
  variant?: 'dialog' | 'inline' | 'static';
  openTo?: 'year' | 'month' | 'day';
  disableToolbar?: boolean;
  fullWidth?: boolean;
  parentComponent?: string;
  isMobile?: boolean;
  labelFunc?: (date: Date | null, invalidLabel: string) => string;
  onChange: (d?: Date) => void | Record<string, unknown>;
  closeCalendarModal?: () => void;
}

const FilterDatePicker = React.memo((props: FilterDatePickerProps) => {
  const { t } = useTranslation();
  const {
    id,
    error,
    noHelper,
    variant,
    openTo = 'day', // Default to 'day' instead of 'date'
    disableToolbar = true,
    labelFunc,
    onChange,
    closeCalendarModal,
    minDate,
    maxDate,
    parentComponent,
    isMobile,
    ...rest
  } = props;

  const renderPickerByParentComponenet = (parentComponent: string) => {
    if (!parentComponent) {
      return (
        <Picker
          data-testid="date-filter-picker"
          {...rest}
          minDate={minDate}
          maxDate={maxDate}
          onChange={onChange}
          id={id}
          disableToolbar={disableToolbar}
          variant={variant}
          autoOk
          format={FORMAT_DATE}
          openTo={openTo}
          labelFunc={labelFunc}
        />
      );
    }

    if (parentComponent === 'mobileFilterWeeks') {
      return (
        <MobilePicker
          data-testid="mobile-week-date-filter-picker"
          {...rest}
          minDate={minDate}
          maxDate={maxDate}
          onChange={onChange}
          id={id}
          disableToolbar={disableToolbar}
          variant={variant}
          autoOk
          format={FORMAT_DATE}
          openTo={openTo}
          labelFunc={labelFunc}
        />
      );
    }
    if (parentComponent === 'mobileFilterMonth') {
      return (
        <MobilePicker
          data-testid="mobile-month-date-filter-picker"
          {...rest}
          minDate={minDate}
          maxDate={maxDate}
          onChange={onChange}
          id={id}
          disableToolbar={disableToolbar}
          variant={variant}
          autoOk
          format={FORMAT_DATE}
          openTo={openTo}
          labelFunc={labelFunc}
        />
      );
    }
  };
  const getTheme = (parentComponent: string) => {
    if (!parentComponent) {
      return theme;
    }

    if (parentComponent === 'mobileFilterWeeks') {
      return activeTheme;
    }
    if (parentComponent === 'mobileFilterMonth') {
      return activeTheme;
    }
  };
  const currentTheme = getTheme(parentComponent);
  return (
    <ThemeProvider theme={currentTheme}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={{
          ...enLocale,
          options: {
            ...enLocale.options,
            weekStartsOn: WEEK_START,
          },
        }}>
        {renderPickerByParentComponenet(parentComponent)}
      </LocalizationProvider>

      {!noHelper && <FormHelperText>{error && t(error.key, error.values)}</FormHelperText>}
    </ThemeProvider>
  );
});

export default FilterDatePicker;
